















import { defineComponent, ref } from "@vue/composition-api";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentSlot } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentSlotProvider";
import useAppointments from "@/hooks/useAppointments";
import useAuthUser from "@/hooks/useAuthUser";
import useFollowing from "@/hooks/useFollowing";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { NotificationData } from "@/types";
import { ERRORS, EVENTS } from "@/const";
import SlotNotAvailableConfirm from "./components/SlotNotAvailableConfirm";
import UserBookAppointmentModalView from "./components/UserBookAppointmentModalView";
import UserConfirmBookingModal from "./components/UserConfirmBookingModal";

export default defineComponent({
  name: "UserBookAppointmentsModal",
  components: { SlotNotAvailableConfirm, UserBookAppointmentModalView, UserConfirmBookingModal },
  props: {
    user: { type: Object as () => UserPublicData, required: true },
  },
  setup(props, { emit }) {
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.APPOINTMENT]);
    const { checkFollowingById, toggle } = useFollowing(ref([]));
    const { authUser } = useAuthUser();
    const {
      availableAppointmentKinds,
      isBookingAvailable,
      checkBookingAvailable,
      bookAppointmentSlot,
    } = useAppointments();

    const loading = ref(false);
    const processing = ref(false);
    const isFollowing = ref(false);
    const isSubscriber = ref(false);
    const appointment = ref<Appointment | null>(null);

    const slotNotification: NotificationData = {
      text: $t("Time slot unavailable 😔 Please choose another one to meet with me 🙏"),
      image: {
        user: props.user,
      },
      timeout: 2000,
      button: {
        text: $t("Ok"),
      },
    };

    const onBookHandler = async (slot: AppointmentSlot, cb?: () => void) => {
      try {
        processing.value = true;
        appointment.value = await bookAppointmentSlot(slot);
        emitter.emit<NotificationData>(EVENTS.NOTIFICATION_SHOW, {
          text: $t("Your purchase was successful"),
          button: { text: $t("Close") },
        });
        !isFollowing.value && (await toggle(props.user.id));

        cb && cb();
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === ERRORS.SPENDING_BALANCE,
            onSuccessCB: () => emitter.emit(EVENTS.BALANCE_REFILL_SHOW),
          },
          {
            comparator: e => e.details?.messageTemplate === ERRORS.SLOT_IS_NOT_AVAILABLE,
            onSuccessCB: () => {
              emitter.emit(EVENTS.NOTIFICATION_SHOW, slotNotification);
            },
          },
        ]);
      } finally {
        processing.value = false;
      }
    };

    const getSubscriptionStatus = async () => {
      if (!authUser.value) return;
      const response = await providers.SubscriptionProvider.search({
        data: { providerId: props.user.id, consumerIds: [authUser.value.id], expired: false },
      });
      isSubscriber.value = response.totalRecords > 0;
    };

    const init = async () => {
      try {
        loading.value = true;
        await Promise.all([
          checkFollowingById(props.user.id).then(res => (isFollowing.value = res)),
          getPaymentPackages(),
          getSubscriptionStatus(),
        ]);
        await checkBookingAvailable(props.user.id, isSubscriber.value);
        if (!isBookingAvailable.value) {
          emit("close");
          return;
        }
      } finally {
        loading.value = false;
      }
    };
    init();

    return {
      loading,
      processing,
      appointment,
      paymentPackages,
      availableAppointmentKinds,
      isSubscriber,
      onBookHandler,
    };
  },
});
